import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import { useMediaQuery, Box } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import Navigation from './components/Navigation';
import HashtagAnalysis from './components/HashtagAnalysis';
import CommentAnalysis from './components/CommentAnalysis';
import ProfileAnalysis from './components/ProfileAnalysis';
import LikeRateAnalysis from './components/LikeRateAnalysis';
import ConversionRateAnalysis from './components/ConversionRateAnalysis';
import Dashboard from './components/Dashboard';
import ContentStrategy from './components/ContentStrategy';

const customTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#8B5CF6',
      light: '#C084FC',
    },
    text: {
      primary: '#1F2937',
      secondary: '#6B7280',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      color: '#1F2937',
    },
    h2: {
      fontWeight: 600,
      color: '#1F2937',
    },
    h3: {
      fontWeight: 600,
      color: '#1F2937',
    },
    h4: {
      color: '#1F2937',
    },
    h5: {
      color: '#1F2937',
    },
    h6: {
      color: '#1F2937',
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.08)',
          background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
          color: '#ffffff',
          padding: '24px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 30,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
        },
        contained: {
          background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #7C3AED 0%, #DB2777 100%)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.08)',
          borderRadius: 16,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.MuiTypography-h4, &.MuiTypography-h5, &.MuiTypography-h6': {
            color: '#1F2937',
          },
        },
      },
    },
  },
});

function App() {
  const appTheme = useTheme();
  const isMobile = useMediaQuery(appTheme.breakpoints.down('sm'));

  return (
    <ThemeProvider theme={customTheme}>
      <CssBaseline />
      <Router>
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
          <Navigation />
          <Box
            sx={{
              flexGrow: 1,
              marginLeft: isMobile ? 0 : '240px', // Space for drawer
              transition: appTheme.transitions.create('margin', {
                easing: appTheme.transitions.easing.sharp,
                duration: appTheme.transitions.duration.leavingScreen,
              }),
            }}
          >
            <Box
              component="main"
              sx={{
                maxWidth: '1400px',
                padding: {
                  xs: appTheme.spacing(2),
                  sm: appTheme.spacing(3),
                  md: appTheme.spacing(4)
                },
                paddingBottom: isMobile ? '80px' : appTheme.spacing(3),
                width: '100%',
              }}
            >
              {/* Add top spacing to account for potential toolbar */}
              <Box sx={{ height: isMobile ? 0 : appTheme.spacing(4) }} />
              <Routes>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/" element={<HashtagAnalysis />} />
                <Route path="/content-strategy" element={<ContentStrategy />} />
                <Route path="/comments" element={<CommentAnalysis />} />
                <Route path="/profile" element={<ProfileAnalysis />} />
                <Route path="/like-rate" element={<LikeRateAnalysis />} />
                <Route path="/conversion" element={<ConversionRateAnalysis />} />
              </Routes>
            </Box>
          </Box>
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;
